import * as $ from 'jquery';

/**
 * Fetch the GEORGRAPHIC locale of this IP from ConsentHub.
 * This is NOT a language locale; this returns a best guess for a LEGAL, GEOGRAPHICAL entity to which the IP belongs.
 *
 * @param basePreferencesUrl The base url for the locale endpoint. This is environment-specific.
 * @param done The callback when the request has finished. If there is a locale, the locale is passed; otherwise, including if there was an error, locale is null.
 */
export function fetchLocale(
  basePreferencesUrl: string,
  done: (locale: string | null) => void,
): void {
  $.get(`${basePreferencesUrl}/rest/forms/locale`)
    .done(body => done(body['locale']))
    .fail(() => {
      $.get('/marketing-consent-error');
      done(null);
    });
}

/**
 * Fetch whether the locale requires we collect consent from an endpoint we maintain in our own app, /marketing-consent-required.
 *
 * @param locale The locale to check for consent requirements.
 * @param done The callback on conclusion of the request. Called with 'required' reflecting whether or not the checkbox is required.
 */
export function doesLocaleRequireGeneralMarketingConsent(
  locale: string | null,
  done: (required: boolean) => void,
): void {
  $.get(`/marketing-consent-required?locale=${locale}`)
    .done(body => {
      done(body.should_display_general_marketing_consent_checkbox);
    })
    .fail(() => done(true)); // err on the side of caution and display it anyway; if it's unnecessary it's ignored anyway.
}

/**
 * Fetch whether the locale requires we collect consent from an endpoint we maintain in our own app, /marketing-consent.
 * This may also possibly auto-consent a user, in which case we can hide the banner.
 *
 * @param locale  The locale to check for consent requirements.
 * @param done The callback when the request completes successfully.
 */
export function fetchConsentState(
  locale: string | null,
  done: (generalMarketingBannerRequired: boolean) => void,
): void {
  $.get(`/marketing-consent?locale=${locale}`)
    .done(body => done(body.should_display_general_marketing_consent_banner))
    .fail(() => done(true)); // err on the side of caution and display it anyway; if it's unnecessary it's ignored anyway.
}

/**
 * Give a decsion whether to grant the generalMarketingOptIn consent or not.
 *
 * @param locale The geographic locale of the user.
 * @param general_marketing_opt_in_result true means the user is giving their consent, false means they are not.
 * @param done The callback when the request has completed successfully.
 */
export function setConsentState(
  locale: string | null,
  general_marketing_opt_in_result: boolean,
  done: (result: boolean) => void,
): void {
  $.post('/marketing-consent', { locale, general_marketing_opt_in_result })
    .done(_body => {
      done(true);
    })
    .fail(_error => done(true));
}

export default {
  fetchLocale,
  doesLocaleRequireGeneralMarketingConsent,
  fetchConsentState,
  setConsentState,
};
